import { Box, Divider, IconButton, ListItemButton } from '@mui/material';
import { Tooltip } from '../Tooltip';
import { EditableSectionRow, EditableSectionRowProps, useEditableSectionRowStyles } from './EditableSectionRow.component';
import { useEditableSectionListContext, useEditableSectionsContext } from './editable-sections.context';
import { Fragment, useMemo } from 'react';
import { Button, ButtonProps } from '../Button';
import { Add, ArrowForwardIos, DeleteOutline } from '@mui/icons-material';
import { useIsMobile } from '@uplift/hooks';

export type EditableSectionRowListProps = {
  /**
    *  List of content to display in each row. The id should be unique and correspond to the id of the resource the row displays.
    */
  contentList: {
    content: React.ReactNode;
    id: string;
  }[];

  /**
    * If not provided, the add button will not be shown.
    *
    * When button is clicked, the edit index will be set to the length of the contentList, so that a new (empty) edit object is concatenated to formik values array.
    * If initial values are needed, concat an object to the initial values array
    * ex: initialValues: { degrees: [ ...profile.degrees, { school: 'initial school', yearGraduated: '', degree: '', fieldOfStudy: '' } ] }
    *
    * In the onSubmit of the formikConfig, check if the editIndex is the length of the contentList, and if so, create a new resource. OR, check if the id is undefined.
    */
  addButtonConfig?: Omit<ButtonProps, 'onClick'> & {
    /**
      * Used to determine the text for the add button.
      *
      * @default 'Add'
      */
    text?: string;
  };

  /**
    * Form component to use for the edit container.
    *
    * Needs to be a component that accepts an `index` prop and form fields should be named with the index.
    */
  form: React.ComponentType<{index: number}>;

  /**
    * Used to determine the label for the edit container based on the current edit index.
    *
    * @param index current edit index
    * @returns string label for the edit container
    */
  getEditLabel?: (index: number) => string;


  /**
    * If not provided, the delete button will not be shown.
    * If provided, the delete button will be shown, unless it is a new edit.
    * Send function to override
    */
  deleteButtonConfig?: Omit<ButtonProps, 'onClick'> & {
    /**
      * Used to determine the delete functionality for the respective edit content.
      *
      * @param id current edit id based on the id sent in the contentList
      * @returns void
      */
    onDelete?: (id: string) => void;
    /**
      * Used to determine the functionality for a delete button click when the delete button is disabled.
      *
      * @returns void
      */
    onDisabledClick?: () => void;
    /**
      * Used to determine the text for the delete button.
      *
      * @default 'Delete'
      */
    text?: string;
    /**
     * Used to add a tooltip wrapper.
     *
     */
    disabledTooltip?: string;
  } | ((id: string) => React.ReactNode);
} & Pick<EditableSectionRowProps, 'label' | 'rowKey' | 'rowFormWrapperProps' | 'editTip'>;

/**
 * Wraps the EditableSectionRow component to display multiple rows of content within one EditableSectionRow.
 * Each row within this EditableSectionRow has its own edit button, that, when clicked, will display the form in the EditableSectionRow, and the content in the row will be replaced with the form.
 *
 * There is an add button that will add a new row to the contentList and display the form in the EditableSectionRow.
 * There is a delete button that will be displayed when the row is in edit mode. If the row is a new edit, the delete button will not be displayed.
 *
 * Unlike the regular EditableSectionRow, the form component needs to accept an index prop, and the form fields should be named with the index.
 *
 * @requires EditableSectionsContext.Provider : use this provider above the EditableSectionContainer
 * @requires EditableSectionListContext.Provider : use this provider in the same place as EditableSectionsContext.Provider
 *
 * As for the formik config, the initial values should be an array.
 * The onSubmit should use the useEditableSectionListContext hook to get the editIndex
 * so that you can access the current edit object in the formik values array.
 *
 * @example const currentEdit = values[editIndex]
 *
 * To determine if the current edit is a new edit, check if the id is undefined OR if the editIndex is the length of the resource list.
 */
export const EditableSectionRowList: React.VFC<EditableSectionRowListProps> = props => {
  const isMobile = useIsMobile('sm');
  const { classes } = useEditableSectionRowStyles();
  const { setEditKey } = useEditableSectionsContext();
  const { editIndex, setEditIndex } = useEditableSectionListContext();

  const onEditRow = (index: number) => {
    setEditIndex(index);
    setEditKey(props.rowKey);
  };

  const deleteButton = useMemo(() => {
    if (editIndex > props.contentList.length - 1 || !props.deleteButtonConfig) {
      return null;
    }

    const deleteButtonConfig = props.deleteButtonConfig;

    if (typeof deleteButtonConfig === 'function') {
      return deleteButtonConfig(props.contentList[editIndex].id);
    }

    const deleteButton = (
      <Box width="fit-content" onClick={() => deleteButtonConfig?.disabled && deleteButtonConfig?.onDisabledClick?.()}>
        <Button
          color="error"
          startIcon={<DeleteOutline />}
          {...deleteButtonConfig}
          onClick={() => deleteButtonConfig.onDelete?.(props.contentList[editIndex].id)}
        >
          {deleteButtonConfig?.text || 'Delete'}
        </Button>
      </Box>
    );

    if (deleteButtonConfig?.disabledTooltip && deleteButtonConfig?.disabled) {
      return (
        <Tooltip placement="top" title={deleteButtonConfig.disabledTooltip}>{deleteButton}</Tooltip>
      );
    }

    return deleteButton;
  }, [ editIndex, props ]);

  const form = useMemo(() => (
    <Fragment>
      <props.form index={editIndex} />
      {deleteButton && (
        <Box mt={4} display={{ sm: 'none' }}>
          <Box mb={2} mx={-2}>
            <Divider />
          </Box>
          <Box display="flex" justifyContent="center">
            {deleteButton}
          </Box>
        </Box>
      )}
    </Fragment>
  ), [ deleteButton, editIndex, props ]);

  return (
    <EditableSectionRow
      rowKey={props.rowKey}
      kind="editable-row"
      label={props.label}
      editTip={props.editTip}
      hideEditButton
      form={form}
      editLabel={props.getEditLabel?.(editIndex)}
      rowFormWrapperProps={{ desktopLeftActions: deleteButton, ...props.rowFormWrapperProps, disableSaveOnInvalid: true }}
    >
      <Box display="flex" flexDirection="column" gap="1.5rem" mt={{ xs: 1, sm: 0 }}>
        {props.contentList.map(({ content }, index) => {

          return (
            <Fragment key={index}>
              {isMobile ? (
                <ListItemButton
                  classes={{ root: classes.noPadding }}
                  onClick={() => onEditRow(index)}
                >
                  <Box flex={1}>
                    {content}
                  </Box>
                  <IconButton color="secondary" onClick={() => onEditRow(index)}>
                    <ArrowForwardIos fontSize="small" />
                  </IconButton>
                </ListItemButton>
              ) : (
                <Box display="flex" alignItems="center">
                  <Box flex={1}>
                    {content}
                  </Box>
                  <Box>
                    <Button color="secondary" onClick={() => onEditRow(index)}>Edit</Button>
                  </Box>
                </Box>
              )}
            </Fragment>
          );
        })}
        {props.addButtonConfig && (
          <Box>
            <Button
              startIcon={<Add />}
              color="primary"
              onClick={() => onEditRow(props.contentList.length)}
              {...props.addButtonConfig}
            >
              {props.addButtonConfig.text || 'Add'}
            </Button>
          </Box>
        )}
      </Box>
    </EditableSectionRow>
  );
};
